import { bind } from 'bind-decorator';
import { ClassroomMembershipRequestPrompt } from 'classrooms/components';
import { ClassroomsApi } from 'global/api';
import { ROUTES } from 'global/constants';
import { LocationDescriptor } from 'history';
import {
    ClassroomDetail,
    ClassroomMembership,
    ClassroomMembershipStatusEnum,
    ItemProgress,
    LearnerReport,
    Profile
} from 'labxchange-client';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import {
    Button,
    Icon, JumpTo,
    KebabMenu,
    KebabMenuItem,
    Modal,
    ModalConfirmation, Pagination, PaginationSizeOptions, SortOptions,
} from 'ui/components';
import { AssignedItemsReportModal } from 'ui/components/AssignedItemsReportModal';
import uiMessages from 'ui/components/displayMessages';
import { showErrorMessage, showSuccessMessage } from 'ui/components/GlobalMessageReporter/dispatch';
import { UserCard, UserCardButton } from 'user/components/UserCard';
import { WrappedMessage } from 'utils';
import messages from './displayMessages';
import { ContentPickerModal } from 'library/components/ContentPickerModal';
import { UI_IS_MD, UI_IS_SM } from 'ui/breakpoints';
import { intl } from 'i18n';
import { Button as EdxButton, InputText } from '@edx/paragon';
import { debounce } from '../../util';

enum ModalTypes {
    None = 0,
    Loading,
    RemoveMembership,
    Assignments,
    AssignContent,
    AcceptAllMemberships,
}

interface Props {
    classroom: ClassroomDetail;
    reloadData: () => void;
}

interface State {
    membershipsPending: ClassroomMembership[];
    membershipsActive: ClassroomMembership[];
    showModal: ModalTypes;
    modalMembership?: ClassroomMembership;
    redirectTo?: LocationDescriptor;
    learnerProgress?: LearnerReport;
    paginationSize: number;
    currentPage: number;
    ordering: string;
    resultCount: number;
    layoutMode: 'desktop'|'mobile'|'tablet';
    isMobileView: boolean;
    isTabletView: boolean;
    searchString: string;
    showMore: boolean;
}

const PAGINATION_OPTIONS = {
    desktop: [
        { label: '21', value: 21 },
        { label: '42', value: 42 },
        { label: '63', value: 63 },
    ],
    tablet: [
        { label: '20', value: 20 },
        { label: '40', value: 40 },
        { label: '60', value: 60 },
    ],
    mobile: []
};


export class ClassroomLearnersEducator extends React.PureComponent<Props, State> {

    constructor(props: any) {
        super(props);

        this.state = {
            membershipsPending: [],
            membershipsActive: [],
            showModal: ModalTypes.None,
            paginationSize: 21,
            currentPage: 1,
            ordering: 'asc',
            resultCount: 0,
            layoutMode: 'desktop',
            isMobileView: false,
            isTabletView: false,
            searchString: '',
            showMore: false
        };
    }

    public async componentDidMount() {
        window.addEventListener('resize', this.setLayoutMode);
        window.addEventListener('resize', this.setLayoutMode);
        this.setLayoutMode();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.setLayoutMode);
        window.removeEventListener('resize', this.setLayoutMode);
        this.debouncedOnSearchChange[1]();
    }

    @bind private setLayoutMode() {
        this.setState({
            layoutMode: UI_IS_SM.matches ? 'mobile' : UI_IS_MD.matches ? 'tablet' : 'desktop',
            isMobileView: UI_IS_SM.matches,
            isTabletView: UI_IS_MD.matches
        }, this.setDefaultPaginationSize);
    }

    private setDefaultPaginationSize() {
        this.setState({
            paginationSize: this.state.layoutMode === 'tablet' ? 20 : this.state.layoutMode === 'mobile' ? 10 : 21
        }, this.fetchMemberships);
    }

    @bind private onSizeSelect(paginationSize: number) {
        this.setState({
            paginationSize
        }, this.fetchActiveMemberships);
    }

    @bind private onSortSelect(ordering: string) {
        this.setState({
            ordering
        }, this.fetchActiveMemberships);
    }

    @bind private onSearchChange(value: string) {
        this.debouncedOnSearchChange[0](value);
    }

    debouncedOnSearchChange = debounce((value: string) => {
        this.setState({
            searchString: value
        }, this.fetchActiveMemberships);
    }, 1000);

    @bind private handleKeyPress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();

        }
    }

    @bind private onSearchClicked() {
        this.setState({});
    }

    @bind private loadMore() {
        this.setState({
            showMore: true,
            currentPage: this.state.currentPage + 1
        }, this.fetchActiveMemberships);
    }

    @bind private onGoToPage(pageNumber: number) {
        if (pageNumber === this.state.currentPage) { return; }

        this.setState({currentPage: pageNumber}, this.fetchActiveMemberships);
    }

    public async fetchMemberships() {
        try {
            const [membershipsPending] = await Promise.all([
                ClassroomsApi.classroomMembershipsListMembershipsForClassroom({
                    id: this.props.classroom.id,
                    status: ClassroomMembershipStatusEnum.Requested,
                    paginationSize: 50 // Default to 50 since we currently not showing pagination for requests
                }),
                this.fetchActiveMemberships(),
            ]);

            this.setState({
                membershipsPending: membershipsPending.results,
            });
        } catch (err) {
            showErrorMessage(<WrappedMessage message={messages.errorMessageFetchMemberships}/>, {
                exception: err,
                details: 'Unable to fetch memberships',
            });
        }
    }

    public async fetchActiveMemberships() {
        const membershipsActive = await ClassroomsApi.classroomMembershipsListMembershipsForClassroom({
            id: this.props.classroom.id,
            status: ClassroomMembershipStatusEnum.Joined,
            paginationSize: this.state.paginationSize,
            currentPage: this.state.currentPage,
            ordering: this.state.ordering,
            searchQuery: this.state.searchString,
        });
        let learners = membershipsActive.results;
        if (this.state.showMore) {
            learners = [...this.state.membershipsActive].concat(learners);
        }
        this.setState({
            membershipsActive: learners,
            resultCount: membershipsActive.count
        }, () => {
            // NOW we can sanitize the page number, because we have the
            // paginationSize and the resultCount set.
            const lastPage = Math.ceil(this.state.resultCount / this.state.paginationSize);
            if (this.state.currentPage > lastPage && lastPage > 0) {
                this.onGoToPage(lastPage);
            }
        });
    }

    public async fetchLearnerProgress(membership: ClassroomMembership) {
        try {
            const learnerProgress = await ClassroomsApi.learnerProgress({
                id: this.props.classroom.id,
                username: membership.user?.username,
            });
            this.setState({learnerProgress});
        } catch (err) {
            showErrorMessage(<WrappedMessage message={messages.errorMessageFetchLearnerProgress}/>, {
                exception: err,
                details: 'Unable to fetch learner progress.',
            });
        }
    }

    public render() {
        const paginationSizeOptions = <PaginationSizeOptions
            name='pagination_size_options'
            value={this.state.paginationSize}
            options={PAGINATION_OPTIONS[this.state.layoutMode]}
            onChange={this.onSizeSelect}
            hidePerPageSuffix={true}
        />;

        const sortOptionsList = [
            { label: intl.formatMessage(messages.ascendingSortLabel), value: 'asc' },
            { label: intl.formatMessage(messages.descendingSortLabel), value: 'desc' }
        ];

        const sortOptions = <SortOptions
            name='sort_options'
            value={this.state.ordering}
            options={sortOptionsList}
            onChange={this.onSortSelect}
            isMobileView={this.state.isMobileView}
        />;

        const searchButton = (
            <EdxButton
                className='search-button btn py-0'
                onClick={this.onSearchClicked} aria-label={intl.formatMessage(messages.searchLearnersPlaceholder)}>
                <Icon name='search' fill='#ffffff' zoom='1.4em'/>
            </EdxButton>
        );

        if (this.state.redirectTo) {
            return <Redirect push={true} to={this.state.redirectTo}/>;
        }
        return (
            <div className='classroom-learners-educator'>
                {
                    this.state.membershipsPending.length > 0 &&
                    <div className='list-header-requests'>
                        <h1>Class enrollment requests</h1>
                        <Button
                            icon='check'
                            onClick={() => this.setState({showModal: ModalTypes.AcceptAllMemberships})}
                            label={messages.acceptAllMembershipsLabel}/>
                    </div>
                }
                {this.renderPendingMembershipCards()}

                <div className='list-header d-flex'>
                    <div className='result-count mr-auto'>
                        <WrappedMessage
                            message={messages.activeLearnersCounter}
                            values={{
                                count: this.state.resultCount,
                            }}
                        />
                    </div>
                    <div className='result-controls'>
                        {!this.state.isMobileView &&
                            <div className='pagination-size-options'>{ paginationSizeOptions }</div>
                        }
                        <div className='options-container'>{ sortOptions }</div>
                        <div className='learners-search'>
                            <InputText
                                name='search_learners_text-input'
                                inline={true}
                                className='search-input'
                                placeholder={intl.formatMessage(this.state.isMobileView ?  messages.searchLearnersPlaceholderMobile : messages.searchLearnersPlaceholder)}
                                aria-label={intl.formatMessage(messages.searchLearnersPlaceholder)}
                                onChange={this.onSearchChange}
                                onKeyPress={this.handleKeyPress}
                                value={this.state.searchString}
                                inputGroupAppend={searchButton}
                            />
                        </div>
                    </div>
                </div>
                {this.renderActiveMembershipCards()}
                {this.renderModals()}
            </div>
        );
    }

    private renderMenu(membership: ClassroomMembership) {
        return (
            <KebabMenu
                darkKebab={true}
            >
                <KebabMenuItem
                    iconName='trashcan'
                    disabled={false}
                    message={messages.removeLearnerButton}
                    onClick={() => { this.onRemove(membership); }}
                />
            </KebabMenu>
        );
    }

    private renderModals() {
        if (this.state.showModal === ModalTypes.None) { return null; }
        switch (this.state.showModal) {
            case ModalTypes.Loading: {
                return <Modal
                    content={<>Loading...</>}
                    onRequestClose={this.onModalClose}
                />;
            }
            case ModalTypes.RemoveMembership: {
                const membership = this.state.modalMembership;
                if (membership === undefined || membership.user === undefined) { return null; }
                return (
                    <ModalConfirmation
                        onCanceled={this.onModalClose}
                        onConfirmed={() => this.removeMembership(membership)}
                        title={messages.removeMembershipModalTitle}
                        body={messages.removeMembershipModalBody}
                        bodyValues={{fullName: membership.user.fullName}}
                        confirmButtonText={uiMessages.uiConfirmationButton}
                        cancelButtonText={uiMessages.uiCancelButton}
                    />
                );
            }
            case ModalTypes.Assignments: {
                const membership = this.state.modalMembership;
                if (membership === undefined || membership.user === undefined) { return; }
                if (this.state.learnerProgress === undefined) { return; }
                return (
                    <AssignedItemsReportModal
                        user={membership.user}
                        itemsProgress={this.state.learnerProgress?.items}
                        completion={{weighedEarned: 5, weighedPossible: 10}}
                        onClickRemove={() => this.onRemove(membership!)}
                        onClose={() => this.setState(
                            {showModal: ModalTypes.None, modalMembership: undefined})
                        }
                        markAsIncomplete={this.markAsIncomplete}
                    />
                );
            }
            case ModalTypes.AssignContent: {
                return <ContentPickerModal
                    onClose={this.onModalClose}
                    multipleSelect={true}
                    onItemsAdd={(items) => this.onAssignItems(items, this.state.modalMembership!)}
                />;
            }
            case ModalTypes.AcceptAllMemberships: {
                const memberships = this.state.membershipsPending;
                if (!memberships) { return null; }
                return (
                    <ModalConfirmation
                        onCanceled={this.onModalClose}
                        onConfirmed={() => this.acceptAllMemberships(memberships)}
                        title={messages.acceptAllMembershipsModalTitle}
                        body={messages.acceptAllMembershipsModalBody}
                        confirmButtonText={messages.acceptAllMembershipsModalConfirmLabel}
                        cancelButtonText={uiMessages.uiCancelButton}
                    />
                );
            }
            default: return null;
        }
    }

    // Similar to active membership card rendering,
    // except UserCard has a prompText and no onCancel (preventing rendering of remove button)

    private renderPendingMembershipCards() {
        const cards = this.state.membershipsPending.map((membership: ClassroomMembership) => {
            if (membership.user === undefined) { return null; }
            return (
                <UserCard
                    key={membership.user.username}
                    profile={membership.user}
                    accessory={
                        <ClassroomMembershipRequestPrompt
                            membership={membership}
                            onComplete={() => { this.fetchMemberships(); }}
                        />
                    }
                    accessoryHeight='auto'
                    highlightBorder={true}
                    threeColumns={true}
                />
            );
        });
        return <div className='user-cards-list-container memberships-list-pending'>{cards}</div>;
    }

    private renderActiveMembershipCards() {
        const pageCount = Math.max(1, Math.ceil(this.state.resultCount / this.state.paginationSize));
        const nextPageExists = pageCount >= this.state.currentPage + 1;

        const cards = this.state.membershipsActive.map((membership: ClassroomMembership) => {
            if (membership.user === undefined) { return null; }

            return (
                <UserCard key={membership.user.username}
                    menu={this.renderMenu(membership)}
                    profile={membership.user}
                    accessory={
                        <>
                            <UserCardButton
                                onClick={() => { this.onClickMessage(membership.user); }}
                                iconName='message2'
                                label={messages.messageButton}
                            />
                            <UserCardButton
                                onClick={() => { this.onClickAssignContent(membership); }}
                                iconName='assign'
                                label={messages.assignContentButton}
                            />
                            <UserCardButton
                                onClick={() => { this.onClickProgress(membership); }}
                                iconName='zap'
                                label={messages.progressButton}
                            />
                        </>
                    }
                    threeColumns={true}
                />
            );
        });
        return <>
            <div className='user-cards-list-container memberships-list'>{cards}</div>
             <div className='bottom-container'>
                 <hr className='divider mb-3 lxc-hide-mobile' />
                <div className='lxc-hide-mobile d-flex justify-content-between mb-3'>
                    <JumpTo
                        pageCount={pageCount}
                        currentPage={this.state.currentPage}
                        onPageSelect={this.onGoToPage}
                    />
                    <Pagination
                        pageCount={pageCount}
                        currentPage={this.state.currentPage}
                        onPageSelect={this.onGoToPage}
                    />
                </div>
            </div>
            <div className='d-md-none'>
                {nextPageExists &&
                    <>
                        <hr className='divider mb-3' />
                        <Button
                            label={messages.showMoreResults}
                            onClick={this.loadMore}
                            fullWidth={true}
                        />
                    </>
                }
            </div>
        </>;
    }

    @bind private async removeMembership(membership: ClassroomMembership) {
        if (this.props.classroom === undefined || membership.user === undefined) {
            this.onModalClose();
            return;
        }
        this.setState({showModal: ModalTypes.Loading}, async () => {
            try {
                await ClassroomsApi.leave({ data: {
                    id: this.props.classroom.id,
                    user: membership.user?.username,
                }});
                this.fetchMemberships();
                this.props.reloadData();
            } catch (err) {
                showErrorMessage(<WrappedMessage message={messages.errorMessageRemoveMembership}/>, {
                    exception: err,
                    details: 'Unable to remove memberships',
                });
            }
            this.onModalClose();
        });
    }

    @bind private async acceptAllMemberships(memberships: ClassroomMembership[]) {
        this.setState({showModal: ModalTypes.Loading}, async () => {
            try {
                await ClassroomsApi.classroomMembershipsAcceptAll({
                    data: {classroom: this.props.classroom.id}},
                );
                this.fetchMemberships();
                this.props.reloadData();
            } catch (err) {
                showErrorMessage(<WrappedMessage message={messages.errorMessageUpdateMembership}/>, {
                    exception: err,
                });
            }
            this.onModalClose();
        });
    }

    @bind private async markAsIncomplete(item: ItemProgress) {
        const membership = this.state.learnerProgress?.membership;
        if (this.state.modalMembership) {
            const user = membership?.user;
            await ClassroomsApi.markIncomplete({ id: item.id, data: {username: user?.username} });
            await this.fetchLearnerProgress(this.state.modalMembership);
        }
    }

    @bind private onRemove(membership: ClassroomMembership) {
        this.setState({
            modalMembership: membership,
            showModal: ModalTypes.RemoveMembership,
        });
    }

    @bind private async onAssignItems(items: Set<string>, membership: ClassroomMembership) {
        this.onModalClose();
        if (membership.id === undefined) { return; }
        try {
            await ClassroomsApi.classroomMembershipsAssign({id: membership.id, data: {
                items: [...items],
            }});
            showSuccessMessage(<WrappedMessage
                message={messages.assignItemSuccess}
                values={membership.user}/>
            );
        } catch (err) {
            showErrorMessage(<WrappedMessage message={messages.errorAssignItem}/>, {
                exception: err,
                details: 'Unable to assign item',
            });
        }
    }

    @bind private onModalClose() {
        this.setState({showModal: ModalTypes.None, modalMembership: undefined});
    }

    @bind private async onClickProgress(membership: ClassroomMembership) {
        await this.fetchLearnerProgress(membership);
        this.setState({showModal: ModalTypes.Assignments, modalMembership: membership});
    }

    @bind private async onClickAssignContent(membership: ClassroomMembership) {
        this.setState({showModal: ModalTypes.AssignContent, modalMembership: membership});
    }

    @bind private onClickMessage(user?: Profile) {
        if (user === undefined) {
            // Shouldn't happen, this is just a hint to TypeScript since the auto-generated class has an optional user.
            throw new Error('missing membership user!');
        }
        this.setState({redirectTo: ROUTES.Messages.MESSAGE_SLUG(user.username)});
    }
}
